import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './BoostCreditDrawdowns.scss'
import { boostCreditBXService } from 'BoostXpressAdmin/Services/boostCreditBXService'
import { BoostCreditDrawdown } from 'BoostXpressAdmin/Model/boostCreditXModel' 
import { HorizontalLoader } from 'Components'
import { CurrencyFormatter, TimeConverter } from 'Helpers' 
 
interface BoostCreditDrawdownsProps {
  userResourceId: string
}

const BoostCreditDrawdowns: React.FC<BoostCreditDrawdownsProps> = ({ userResourceId }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [drawdowns, setDrawdowns] = useState<BoostCreditDrawdown[]>([])
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)

  // Filter states
  const [statusFilter, setStatusFilter] = useState('')
  const [coverageFilter, setCoverageFilter] = useState('')
  const [hasOutstandingFilter, setHasOutstandingFilter] = useState('')

  const loadDrawdowns = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: any = {
        page: pageNumber,
        limit: 50,
        sortOrder: 'DESC',
        userResourceId,
      }

      if (statusFilter) params.status = statusFilter
      if (coverageFilter) params.coverage = coverageFilter
      if (hasOutstandingFilter) params.hasOutstandingBalance = hasOutstandingFilter === 'true'

      const res = await boostCreditBXService.getDrawdowns(params)

      console.log({WWWyu132432423drwerwe:res.data.data})
      setDrawdowns(res.data.data || [])
      setTotal(res.data.meta?.pagination?.total || 0)
    } catch (e: any) {
      setError(e.message || 'Failed to load drawdowns')
      setDrawdowns([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDrawdowns()
  }, [pageNumber, statusFilter, coverageFilter, hasOutstandingFilter, userResourceId])

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'owing':
        return '#f59e0b'
      case 'partially_repaid':
        return '#3b82f6'
      case 'repaid':
        return '#10b981'
      case 'overdue':
        return '#ef4444'
      case 'defaulted':
        return '#dc2626'
      default:
        return '#6b7280'
    }
  }

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'owing':
        return '#fef3c7'
      case 'partially_repaid':
        return '#dbeafe'
      case 'repaid':
        return '#d1fae5'
      case 'overdue':
        return '#fee2e2'
      case 'defaulted':
        return '#fecaca'
      default:
        return '#f3f4f6'
    }
  }

  return (
    <section className='bx-details-section'>
      <h4 className='bx-details-section-title'>Boost Flex Usage History</h4>
      <div className='bx-transactions-history-box'>
        <div className='bx-history-toolbar-wrap row g-2 align-items-center'>
          <div className='col-12 col-md-6 col-xxl-4'>
            <select
              className='form-control'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value=''>All Statuses</option>
              <option value='owing'>Owing</option>
              <option value='partially_repaid'>Partially Repaid</option>
              <option value='repaid'>Repaid</option>
              <option value='overdue'>Overdue</option>
              <option value='defaulted'>Defaulted</option>
            </select>
          </div>
          <div className='col-12 col-md-3 col-xxl-2'>
            <select
              className='form-control'
              value={coverageFilter}
              onChange={(e) => setCoverageFilter(e.target.value)}
            >
              <option value=''>All Coverage</option>
              <option value='full'>Full</option>
              <option value='partial'>Partial</option>
            </select>
          </div>
          <div className='col-12 col-md-3 col-xxl-2'>
            <select
              className='form-control'
              value={hasOutstandingFilter}
              onChange={(e) => setHasOutstandingFilter(e.target.value)}
            >
              <option value=''>Outstanding Balance</option>
              <option value='true'>Has Outstanding</option>
              <option value='false'>No Outstanding</option>
            </select>
          </div>
        </div>
        <div className='bx-history-table-wrapper'>
          <table className='bx-history-table'>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Principal</th>
                <th>Outstanding</th>
                <th>Paid</th>
                <th>Coverage</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className='text-center'>
                    <HorizontalLoader />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className='text-center'>
                    <div className='alert alert-danger'>{error}</div>
                  </td>
                </tr>
              ) : drawdowns.length === 0 ? (
                <tr>
                  <td colSpan={8} className='text-center'>
                    No drawdowns found
                  </td>
                </tr>
              ) : (
                drawdowns.map((d: BoostCreditDrawdown, index) => (
                  <tr key={d.resourceId}>
                    <td>{Number(index) + 1 + (pageNumber - 1) * 50}</td>
                    <td className='bx-amount-cell'>₦{CurrencyFormatter(d.principalMajor)}</td>
                    <td className='bx-amount-cell'>₦{CurrencyFormatter(d.outstandingMajor)}</td>
                    <td className='bx-amount-cell'>₦{CurrencyFormatter(d.paidMajor)}</td>
                    <td>
                      <span
                        className='bx-coverage-badge'
                        style={{
                          backgroundColor: d.coverage === 'full' ? '#d1fae5' : '#fef3c7',
                          color: d.coverage === 'full' ? '#059669' : '#d97706',
                        }}
                      >
                        {d.coverage.toUpperCase()}
                      </span>
                    </td>
                    <td>{TimeConverter(d.dueDate)}</td>
                    <td>
                      <span
                        className='bx-status-badge'
                        style={{
                          backgroundColor: getStatusBgColor(d.status),
                          color: getStatusBadgeColor(d.status),
                        }}
                      >
                        {d.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <button
                        className='bx-view-btn'
                        onClick={() => navigate(`/boostxpress/orders/${d.orderResourceId}`)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {total > 50 && (
          <div className='bx-pagination mt-3'>
            <button
              className='bx-pagination-btn'
              disabled={pageNumber <= 1 || loading}
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className='bx-pagination-info'>
              Page {pageNumber} of {Math.ceil(total / 50)}
            </span>
            <button
              className='bx-pagination-btn'
              disabled={pageNumber >= Math.ceil(total / 50) || loading}
              onClick={() => setPageNumber((p) => Math.min(Math.ceil(total / 50), p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default BoostCreditDrawdowns
